import { useMemo } from 'react';
import { Lock, Play, ChevronRight, Crown, Wrench } from 'lucide-react';
import { FD_LEVELS, WORLDS, MASTER_UNLOCK_LEVEL } from '@/lib/focusDriveLevels';
import StarBadge from '@/components/lounge/StarBadge';

/**
 * FocusDriveLevelSelect — Apple-Arcade-style world+level map for Focus Drive.
 * 7 worlds, 100 levels. Mirrors Mind Over City's LevelSelect but wider (15 per world).
 */
export default function FocusDriveLevelSelect({ fd, onPick, onGarage, onMaster }) {
  const grouped = useMemo(() => WORLDS.map(w => ({
    ...w,
    levels: FD_LEVELS.filter(l => l.world === w.id),
  })), []);
  const highestUnlocked = fd?.highestUnlocked ?? 1; // gated: clear a level to unlock the next
  const masterUnlocked  = true;

  return (
    <div className="space-y-4" data-testid="fd-level-select">
      {/* Garage quick-access strip */}
      <button type="button" onClick={onGarage} data-testid="fd-open-garage"
        className="w-full rounded-2xl px-4 py-3 flex items-center justify-between bg-gradient-to-r from-navy to-[#12253F] border border-gold/20 hover:border-gold/50 transition-all hover:-translate-y-0.5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center">
            <Wrench size={16} className="text-gold" strokeWidth={2}/>
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.28em] text-gold font-semibold">Stripped Garage</p>
            <p className="font-heading text-base text-white">
              {(fd?.garage?.skinsUnlocked?.length ?? 1)} / 4 skins · {(fd?.garage?.trailsUnlocked?.length ?? 1)} / 4 trails
            </p>
          </div>
        </div>
        <ChevronRight size={16} className="text-white/60"/>
      </button>

      {grouped.map((w) => (
        <div key={w.id} className="rounded-2xl p-4"
             style={{ background: `linear-gradient(135deg, ${w.color} 0%, ${adjust(w.color, -8)} 100%)` }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: w.accent }}>
                World {w.id}
              </p>
              <h4 className="font-heading text-lg text-white mt-0.5">{w.name}</h4>
              <p className="text-white/55 text-xs mt-0.5">{w.tagline}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-widest text-white/40">Stars</p>
              <p className="font-heading text-base text-white tabular-nums">
                {w.levels.reduce((sum, l) => sum + (fd?.levelStars?.[l.id] ?? 0), 0)}
                <span className="text-white/40">/{w.levels.length * 3}</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {w.levels.map((l) => {
              const stars = fd?.levelStars?.[l.id] ?? 0;
              const locked = l.id > highestUnlocked;
              return (
                <button
                  key={l.id}
                  type="button"
                  disabled={locked}
                  onClick={() => onPick(l.id)}
                  data-testid={`fd-level-${l.id}`}
                  className={`relative rounded-lg px-1 py-1.5 text-center transition-all border ${
                    locked
                      ? 'bg-white/5 border-white/5 cursor-not-allowed'
                      : stars > 0
                        ? 'bg-white/10 border-white/15 hover:bg-white/15 hover:-translate-y-0.5'
                        : 'bg-white/[0.06] border-white/10 hover:bg-white/10 hover:-translate-y-0.5'
                  }`}
                >
                  <p className={`font-heading text-sm tabular-nums leading-none ${locked ? 'text-white/25' : 'text-white'}`}>
                    {l.id}
                  </p>
                  <div className="mt-1 flex items-center justify-center min-h-[10px]">
                    {locked ? <Lock size={9} className="text-white/25" strokeWidth={2}/> : <StarBadge count={stars} size={7} testid={`fd-level-${l.id}-stars`}/>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Master Drive unlock */}
      <button
        type="button"
        onClick={onMaster}
        data-testid="fd-master-mode"
        className="w-full rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#0B1E29] to-[#134058] border-gold/30 hover:border-gold/60 hover:-translate-y-0.5"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold flex items-center gap-1.5">
              <Crown size={12} strokeWidth={2}/> Master Drive
            </p>
            <p className="font-heading text-base mt-1 text-white">The hardest run of your life</p>
            <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
              Fastest speeds, tightest traffic, aurora highway. No timer, no safety — how long can you hold the line?
            </p>
          </div>
          <Play size={16} className="text-gold mt-1 shrink-0" strokeWidth={2}/>
        </div>
        {(fd?.masterBest ?? 0) > 0 && (
          <p className="text-white/60 text-xs mt-2">Best: <span className="text-gold tabular-nums">{fd.masterBest}m</span></p>
        )}
      </button>
    </div>
  );
}

function adjust(hex, amt) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  let r = (num >> 16) + amt;
  let g = ((num >> 8) & 0xff) + amt;
  let b = (num & 0xff) + amt;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
